import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFirestore, collection, query, getDocs, where } from 'firebase/firestore';

import { useAuthState } from 'react-firebase-hooks/auth';
import app from '@/services/firebase';
import auth from '@/services/auth';
import { getBootcampBySlug, getActiveBootcamps } from '@/services/bootcamp';
import type { Bootcamp } from '@/types/bootcamp';

import { ButtonGroup, Button } from '@progress/kendo-react-buttons';
import { Button as FordButton, Typography } from '@ford/ford-ui-components';
import { Model, slk } from 'survey-core';
import { Tabulator } from 'survey-analytics/survey.analytics.tabulator';

// Types for vehicle model data
interface VehicleModel {
  model_id: number;
  model: string;
  make_id: number;
}

import 'survey-analytics/survey.analytics.min.css';
import 'tabulator-tables/dist/css/tabulator.min.css';
import 'survey-analytics/survey.analytics.tabulator.min.css';

import cartJSONRaw from '@/pages/bootcamp/baseSurvey.json';
import preSurveyJSONRaw from '@/pages/pre/PreSurvey.json';
import postSurveyJSONRaw from '@/pages/post/PostSurvey.json';

import styles from './_index.module.css';

slk('NDBhNThlYzYtN2EwMy00ZTgxLWIyNGQtOGFkZWJkM2NlNjI3OzE9MjAyNS0wNy0xOSwyPTIwMjUtMDctMTksND0yMDI1LTA3LTE5');

/**
 * Merges pre-survey and post-survey JSON schemas for combined display.
 * Post-survey elements are interleaved after their corresponding pre-survey elements
 * (matched by removing "Post" suffix from field names).
 * Post-survey question names are prefixed with 'post_' to avoid collisions.
 */
function mergeSurveySchemas(preJSON: any, postJSON: any): any {
  const combined = JSON.parse(JSON.stringify(preJSON));
  const postClone = JSON.parse(JSON.stringify(postJSON));

  // Helper to prefix element names
  const prefixElement = (el: any) => {
    if (el.name) {
      el.name = `post_${el.name}`;
    }
    if (el.elements) {
      el.elements.forEach(prefixElement);
    }
    if (el.rows) {
      el.rows.forEach((row: any) => {
        if (row.value) row.value = `post_${row.value}`;
      });
    }
  };

  // Prefix all post-survey elements
  postClone.pages.forEach((page: any) => {
    page.elements?.forEach(prefixElement);
  });

  // Get all post elements (flattened from first page)
  const postElements = postClone.pages[0]?.elements || [];

  // Build a map of post elements by their "base" name (without "Post" suffix and without "post_" prefix)
  const postElementsByBaseName = new Map<string, any>();
  const unmatchedPostElements: any[] = [];

  postElements.forEach((el: any) => {
    // el.name is now "post_fieldNamePost" - extract base name
    const prefixedName = el.name || '';
    const originalName = prefixedName.replace(/^post_/, ''); // e.g., "confidentAbilityToTowPost"
    const baseName = originalName.replace(/Post$/, ''); // e.g., "confidentAbilityToTow"

    if (baseName !== originalName) {
      // This field has a "Post" suffix, so it might have a pre-survey pair
      postElementsByBaseName.set(baseName, el);
    } else {
      // No "Post" suffix - it's a post-only field
      unmatchedPostElements.push(el);
    }
  });

  // Helper to insert post elements after matching pre elements
  const interleaveElements = (elements: any[]): any[] => {
    const result: any[] = [];

    elements.forEach((el: any) => {
      // Handle nested elements (panels)
      if (el.elements) {
        el.elements = interleaveElements(el.elements);
      }

      result.push(el);

      // Check if there's a matching post element to insert after this one
      const elName = el.name || '';
      const matchingPost = postElementsByBaseName.get(elName);
      if (matchingPost) {
        result.push(matchingPost);
        postElementsByBaseName.delete(elName); // Mark as used
      }
    });

    return result;
  };

  // Interleave post elements into pre-survey structure
  combined.pages.forEach((page: any) => {
    if (page.elements) {
      page.elements = interleaveElements(page.elements);
    }
  });

  // Collect any remaining unmatched post elements (both paired ones that didn't find a match and post-only fields)
  const remainingPostElements = [...postElementsByBaseName.values(), ...unmatchedPostElements];

  // Add remaining post elements as a new page at the end
  if (remainingPostElements.length > 0) {
    combined.pages.push({
      name: 'post_additionalQuestions',
      title: 'Post-Survey Questions',
      elements: remainingPostElements,
    });
  }

  combined.title = 'Combined Pre & Post Survey Data';

  return combined;
}

function DashboardScreen() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  // Bootcamp state
  const [bootcamp, setBootcamp] = useState<Bootcamp | null>(null);
  const [bootcampLoading, setBootcampLoading] = useState(true);
  const [activeBootcamps, setActiveBootcamps] = useState<Bootcamp[]>([]);

  const [whichData, setWhichData] = useState<'cart' | 'survey'>('survey');
  const [user, userLoading, userError] = useAuthState(auth);
  const [thisSurvey, setThisSurvey] = useState<Model>();
  const [allAnswers, setAllAnswers] = useState<any[]>();
  const [surveyDataTable, setSurveyDataTable] = useState<Tabulator>();

  const db = getFirestore(app, import.meta.env.VITE_DB_NAME);

  // Derived value - use bootcamp.id for queries (matches what registration writes)
  const eventID = bootcamp?.id || '';

  // Fetch bootcamp data
  useEffect(() => {
    if (!slug) return;

    setBootcampLoading(true);
    getBootcampBySlug(slug)
      .then(data => {
        setBootcamp(data);
        setBootcampLoading(false);
      })
      .catch(err => {
        console.error('Error fetching bootcamp:', err);
        setBootcampLoading(false);
      });
  }, [slug]);

  // Fetch active bootcamps for "Did you mean?" suggestions
  useEffect(() => {
    if (!bootcamp && !bootcampLoading) {
      getActiveBootcamps().then(setActiveBootcamps).catch(console.error);
    }
  }, [bootcamp, bootcampLoading]);

  useEffect(() => {
    if (userError) console.error(userError);
  }, [userError]);

  useEffect(() => {
    if (userLoading) return;

    if (!user) {
      navigate('./login');
    }
  }, [userLoading, navigate, user]);

  // Build a lookup from bookeoProductId to product name and price
  const productLookup = useMemo(() => {
    if (!bootcamp) return { byBookeoId: {} as Record<string, { name: string; key: string }> };
    const byBookeoId: Record<string, { name: string; key: string }> = {};
    Object.entries(bootcamp.products).forEach(([key, product]) => {
      if (product.bookeoProductId) {
        byBookeoId[product.bookeoProductId] = { name: product.name, key };
      }
    });
    return { byBookeoId };
  }, [bootcamp]);

  // Attendee lookup: cartID → attendeeID
  const [attendeeLookup, setAttendeeLookup] = useState<Record<string, string>>({});

  // Vehicle model lookup: model_id → model name
  const [modelLookup, setModelLookup] = useState<Record<number, string>>({});

  // Fetch attendees for cartID → attendeeID mapping
  useEffect(() => {
    if (!eventID) return;

    const fetchAttendees = async () => {
      const attendeesRef = collection(db, 'attendees');
      const q = query(attendeesRef, where('eventID', '==', eventID));
      const snapshot = await getDocs(q);

      const lookup: Record<string, string> = {};
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data.cartID) {
          lookup[data.cartID] = docSnap.id;
        }
      });
      setAttendeeLookup(lookup);
    };

    fetchAttendees().catch(console.error);
  }, [eventID, db]);

  // Fetch vehicle models for model_id → model name mapping
  useEffect(() => {
    if (whichData !== 'survey') return;
    if (Object.keys(modelLookup).length > 0) return; // Already fetched

    fetch('https://cdn.latitudewebservices.com/data/models.json')
      .then(response => response.json())
      .then((data: VehicleModel[]) => {
        const lookup: Record<number, string> = {};
        data.forEach(model => {
          lookup[model.model_id] = model.model;
        });
        setModelLookup(lookup);
      })
      .catch(console.error);
  }, [whichData, modelLookup]);

  // Fetch data when bootcamp is loaded and whichData changes
  useEffect(() => {
    if (!eventID || !bootcamp) return;

    if (whichData === 'cart') {
      const surveyJSON = JSON.parse(JSON.stringify(cartJSONRaw));
      // add a couple fields to the survey json
      surveyJSON.pages[0].elements.unshift(
        {
          type: 'text',
          name: 'attendeeID',
          title: 'Attendee ID',
          visible: false,
        },
        {
          type: 'text',
          name: 'cartID',
          title: 'Cart ID',
          visible: false,
        },
        {
          type: 'text',
          name: 'cartDate',
          inputType: 'datetime-local',
          visible: false,
        },
      );

      // Update selectedModule to be a text field with proper title for display
      const moduleSelectionPage = surveyJSON.pages.find((p: any) => p.name === 'moduleSelection');
      if (moduleSelectionPage) {
        const moduleElement = moduleSelectionPage.elements.find((e: any) => e.name === 'selectedModule');
        if (moduleElement) {
          moduleElement.type = 'text';
          moduleElement.title = 'Selected Module';
          moduleElement.renderAs = undefined;
        }

        // Convert priceTotal from expression to text for display
        const priceTotalElement = moduleSelectionPage.elements.find((e: any) => e.name === 'priceTotal');
        if (priceTotalElement) {
          priceTotalElement.type = 'text';
          priceTotalElement.title = 'Total Price';
          priceTotalElement.expression = undefined;
          priceTotalElement.displayStyle = undefined;
        }
      }

      const survey = new Model(surveyJSON);
      setThisSurvey(survey);

      // get the answers
      const answersRef = collection(db, 'carts');
      const q = query(answersRef, where('eventID', '==', eventID));
      getDocs(q).then(querySnapshot => {
        const answers: any[] = [];

        querySnapshot.forEach(docSnap => {
          const data = docSnap.data();
          // Map selectedModule (bookeoProductId) to human-readable name
          const moduleInfo = productLookup.byBookeoId[data.selectedModule];
          const moduleName = moduleInfo?.name || data.selectedModule || 'Unknown';

          // Format price as currency
          const priceFormatted = data.priceTotal != null ? `$${Number(data.priceTotal).toFixed(2)}` : '$0.00';

          // Look up attendeeID from cartID
          const attendeeID = attendeeLookup[docSnap.id] || '';

          answers.push({
            attendeeID,
            cartID: docSnap.id,
            ...data,
            selectedModule: moduleName,
            priceTotal: priceFormatted,
            displayName: data.name,
          });
        });

        setAllAnswers(answers);
      });
    }
    if (whichData === 'survey') {
      // Merge pre and post survey schemas with prefixed post-survey names
      const combinedJSON = mergeSurveySchemas(preSurveyJSONRaw, postSurveyJSONRaw);

      // Add metadata fields to the combined schema
      combinedJSON.pages[0].elements.unshift(
        {
          type: 'text',
          name: 'attendeeID',
          title: 'Attendee ID',
          visible: false,
        },
        {
          type: 'text',
          name: 'preSurveyID',
          title: 'Pre-Survey ID',
          visible: false,
        },
        {
          type: 'text',
          name: 'postSurveyID',
          title: 'Post-Survey ID',
          visible: false,
        },
        {
          type: 'text',
          name: 'preSurveyDate',
          title: 'Pre-Survey Date',
          inputType: 'datetime-local',
          visible: false,
        },
        {
          type: 'text',
          name: 'postSurveyDate',
          title: 'Post-Survey Date',
          inputType: 'datetime-local',
          visible: false,
        },
      );

      const survey = new Model(combinedJSON);
      setThisSurvey(survey);

      // Fetch all surveys and join pre + post by attendeeID
      const answersRef = collection(db, 'surveys');
      const q = query(answersRef, where('eventID', '==', eventID));
      getDocs(q).then(querySnapshot => {
        // Group surveys by attendeeID
        const surveysByAttendee = new Map<string, { pre?: any; post?: any }>();

        querySnapshot.forEach(docSnap => {
          const data = docSnap.data();
          const attendeeID = data.attendeeID;

          // Skip surveys without attendeeID
          if (!attendeeID) {
            console.warn('Survey missing attendeeID:', docSnap.id);
            return;
          }

          const existing = surveysByAttendee.get(attendeeID) || {};
          // Get milliseconds for comparison (surveyDate is a JS Date object stored as string)
          const surveyDateMs = data.surveyDate ? new Date(data.surveyDate).getTime() : 0;

          if (data.surveyType === 'preTD') {
            // Take latest pre-survey if multiple exist
            const existingMs = existing.pre?._surveyDateMs || 0;
            if (!existing.pre || surveyDateMs > existingMs) {
              existing.pre = { ...data, docId: docSnap.id, _surveyDateMs: surveyDateMs };
            }
          } else if (data.surveyType === 'postTD') {
            // Take latest post-survey if multiple exist
            const existingMs = existing.post?._surveyDateMs || 0;
            if (!existing.post || surveyDateMs > existingMs) {
              existing.post = { ...data, docId: docSnap.id, _surveyDateMs: surveyDateMs };
            }
          }

          surveysByAttendee.set(attendeeID, existing);
        });

        // Flatten into combined rows
        const answers: any[] = [];
        surveysByAttendee.forEach(({ pre, post }, attendeeID) => {
          // Transform vehicleDrivenMostModel from ID to name (pre-survey field)
          let vehicleModelName = pre?.vehicleDrivenMostModel;
          if (pre?.vehicleDrivenMostModel && modelLookup[pre.vehicleDrivenMostModel]) {
            vehicleModelName = modelLookup[pre.vehicleDrivenMostModel];
          }

          // Helper to format surveyDate (stored as ISO string) to readable date
          const formatDate = (dateValue: any): string => {
            if (!dateValue) return '';
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';
            return date.toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            });
          };

          // Build combined row with pre-survey fields (unprefixed) and post-survey fields (prefixed)
          const combinedRow: any = {
            attendeeID,
            preSurveyID: pre?.docId || '',
            postSurveyID: post?.docId || '',
            preSurveyDate: formatDate(pre?.surveyDate),
            postSurveyDate: formatDate(post?.surveyDate),
            displayName: pre?.name || post?.name || '',
          };

          // Add all pre-survey fields (unprefixed)
          if (pre) {
            Object.entries(pre).forEach(([key, value]) => {
              const excludeKeys = ['docId', 'surveyType', 'eventID', 'attendeeID', 'surveyDate', '_surveyDateMs'];
              if (!excludeKeys.includes(key)) {
                combinedRow[key] = key === 'vehicleDrivenMostModel' ? vehicleModelName : value;
              }
            });
          }

          // Add all post-survey fields with 'post_' prefix
          if (post) {
            Object.entries(post).forEach(([key, value]) => {
              const excludeKeys = [
                'docId',
                'surveyType',
                'eventID',
                'attendeeID',
                'surveyDate',
                '_surveyDateMs',
                'name',
              ];
              if (!excludeKeys.includes(key)) {
                combinedRow[`post_${key}`] = value;
              }
            });
          }

          answers.push(combinedRow);
        });

        setAllAnswers(answers);
      });
    }
  }, [whichData, eventID, db, bootcamp, productLookup, attendeeLookup, modelLookup]);

  useEffect(() => {
    if (!thisSurvey || !allAnswers) return;

    const surveyDataTable = new Tabulator(thisSurvey, allAnswers, {
      downloadButtons: ['xlsx', 'csv', 'pdf'],
      useNamesAsTitles: true,
    });

    setSurveyDataTable(surveyDataTable);
  }, [thisSurvey, allAnswers]);

  useEffect(() => {
    if (!surveyDataTable) return;

    surveyDataTable.render('surveyDataTable');
    return () => {
      const el = document.getElementById('surveyDataTable');
      if (el) el.innerHTML = '';
    };
  }, [surveyDataTable]);

  // Conditional returns AFTER all hooks (Rules of Hooks compliance)
  if (bootcampLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (!bootcamp) {
    return (
      <div className={styles.container}>
        <div className={styles.notFound}>
          <Typography displayStyle="display-2-small-regular">Bootcamp "{slug}" not found</Typography>
          <br />
          <Typography displayStyle="body-1-regular">Did you mean?</Typography>
          <div className={styles.buttonsContainer}>
            {activeBootcamps.map(b => (
              <FordButton key={b.slug} onClick={() => (window.location.href = `/bootcamp/${b.slug}/dashboard`)}>
                {b.name}
              </FordButton>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Typography displayStyle="display-2-small-regular">{bootcamp.name} Dashboard</Typography>
      </div>
      <div className={styles.tabs}>
        <ButtonGroup>
          <Button themeColor={whichData === 'cart' ? 'tertiary' : 'base'} onClick={() => setWhichData('cart')}>
            Carts
          </Button>
          <Button themeColor={whichData === 'survey' ? 'tertiary' : 'base'} onClick={() => setWhichData('survey')}>
            Surveys
          </Button>
        </ButtonGroup>
      </div>
      <div className={styles.count}>
        <Typography displayStyle="body-1-regular">Total: {allAnswers?.length || 0}</Typography>
      </div>

      <div id="surveyDataTable" />
    </div>
  );
}

export default DashboardScreen;
